import { FC } from 'react';
import Modal from '../../components/shared/Modal/Modal';
import { useTranslation } from 'react-i18next';
import { ReactComponent as TriangleWarningIcon } from '../../assets/svg/triangleWarningIcon.svg';

type Props = {
  show: boolean;
  setShow: (value: boolean) => void;
  /** Names of the selected suppliers that have no contact person. */
  supplierNames: string[];
  /** Whether the save is also sending the latest bid request — decides what gets skipped. */
  isSendingBidRequest: boolean;
  onConfirm: () => void;
  isSaving?: boolean;
};

/**
 * Last stop before a save that includes suppliers nothing can be sent to. It confirms rather than
 * blocks: adding them is a legitimate thing to want (an imported register often has no contacts
 * yet), the user just has to know the bid request won't reach them.
 */
const MissingContactPersonsModal: FC<Props> = ({
  show,
  setShow,
  supplierNames,
  isSendingBidRequest,
  onConfirm,
  isSaving,
}) => {
  const [t] = useTranslation('supplierDatabase', { keyPrefix: 'selectMode.missingContactPersonsModal' });

  return (
    <Modal
      show={show}
      setShow={setShow}
      isScrollable
      title={t('title', {
        count: supplierNames.length,
        defaultValue_one: '{{count}} leverantör saknar kontaktperson',
        defaultValue_other: '{{count}} leverantörer saknar kontaktperson',
      })}
      cancelButtonText={t('cancelButton', 'Avbryt')}
      buttonInfo={{
        label: isSendingBidRequest
          ? t('confirmButton_withBidRequest', 'Lägg till och skicka ändå')
          : t('confirmButton', 'Lägg till ändå'),
        action: onConfirm,
        loaderShow: isSaving,
      }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', fontSize: '14px', color: 'var(--gray-700)' }}>
        <p>
          {isSendingBidRequest
            ? t(
              'description_withBidRequest',
              'Förfrågningsunderlaget kan inte skickas till en leverantör utan kontaktperson. Följande leverantörer läggs till i upphandlingen, men hoppas över när underlaget skickas:'
            )
            : t(
              'description',
              'Följande leverantörer läggs till i upphandlingen, men det går inte att skicka något förfrågningsunderlag till dem förrän de har en kontaktperson:'
            )}
        </p>
        <ul style={{ display: 'flex', flexDirection: 'column', gap: '8px', margin: 0, paddingLeft: 0, listStyle: 'none' }}>
          {supplierNames.map(name => (
            <li key={name} style={{ display: 'flex', alignItems: 'center', gap: '8px', color: 'var(--gray-900)' }}>
              <TriangleWarningIcon style={{ width: '16px', flexShrink: 0, fill: 'var(--yellow-800)' }} />
              {name}
            </li>
          ))}
        </ul>
        <p>
          {t('hint', 'Du kan lägga till kontaktpersoner på leverantören i leverantörsregistret, eller direkt i förfrågningsunderlaget.')}
        </p>
      </div>
    </Modal>
  );
};

export default MissingContactPersonsModal;
